import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NumberModel } from '../primitive/number.model';
import { TextModel } from '../primitive/text.model';
import { AddressModel } from './address.model';
import { CompositeModel } from './composite.model';
import { DropdownModel } from './dropdown.model';
import { isDefined } from '../../utilities/is-defined';
import { LogInstanceToken } from '@evorait/pace-logging';

export class CseDopModel extends CompositeModel {
    public addrNumber: TextModel;
    public building: TextModel;
    public city: TextModel;
    public contactPerson: TextModel;
    public department: TextModel;
    public dropOffPoint: TextModel;
    public floor: TextModel;
    public frontId: string;
    public name1: TextModel;
    public name2: TextModel;
    public name1Long: string;
    public name2Long: string;
    public phone: TextModel;
    public pos: NumberModel;
    public postalCode: TextModel;
    public region: TextModel;
    public room: TextModel;
    public state: TextModel;
    public street: TextModel;
    public statesOfUSA: DropdownModel<TextModel> | undefined;

    public constructor(
        dopNumber = '',
        frontId = '',
        name1 = '',
        name2 = '',
        name1short = '',
        name2short = '',
        street = '',
        postcode = '',
        city = '',
        state = '',
        phone = '',
        department = '',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        sortNumber = 0,
        region = '',
        floor = '',
        building = '',
        room = '',
        contactPerson = '',
        addrNumber = '',
    ) {
        super('CseDopModel');
        this.addrNumber = new TextModel('AddressNumber', addrNumber);
        this.building = <TextModel>this.set(new TextModel('Building', building));
        this.city = <TextModel>this.set(new TextModel('City', city));
        this.contactPerson = <TextModel>this.set(new TextModel('ContactPerson', contactPerson));
        this.department = <TextModel>this.set(new TextModel('Department', department));
        this.dropOffPoint = new TextModel('DropOfPoint', dopNumber);
        this.floor = <TextModel>this.set(new TextModel('Floor', floor));
        this.frontId = frontId;
        const dopName1 = name1short.length > 0 ? name1short : name1;
        this.name1 = <TextModel>this.set(new TextModel('Name1', dopName1));
        const dopName2 = name2short.length > 0 ? name2short : name2;
        this.name2 = <TextModel>this.set(new TextModel('Name2', dopName2));
        this.phone = <TextModel>this.set(new TextModel('Phone', phone));
        this.pos = new NumberModel('Position', undefined);
        this.postalCode = <TextModel>this.set(new TextModel('PostCode', postcode));
        this.region = <TextModel>this.set(new TextModel('Region', region));
        this.room = <TextModel>this.set(new TextModel('Room', room));
        this.state = new TextModel('State', state);
        this.street = <TextModel>this.set(new TextModel('Street', street));
        this.name1Long = name1;
        this.name2Long = name2;
    }

    public setFieldValidations(screenName: string): void {
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        modelOptionsLoader.load(this.name1, screenName, 'Name1');
        modelOptionsLoader.load(this.name2, screenName, 'Name2');
        modelOptionsLoader.load(this.street, screenName, 'Street');
        modelOptionsLoader.load(this.postalCode, screenName, 'PostCode');
        modelOptionsLoader.load(this.city, screenName, 'City');
        modelOptionsLoader.load(this.building, screenName, 'Building');
        modelOptionsLoader.load(this.room, screenName, 'Room');
        modelOptionsLoader.load(this.floor, screenName, 'Floor');
        modelOptionsLoader.load(this.department, screenName, 'Department');
        modelOptionsLoader.load(this.phone, screenName, 'Phone');
        modelOptionsLoader.load(this.contactPerson, screenName, 'ContactPerson');
        if (isDefined(this.statesOfUSA)) {
            modelOptionsLoader.load(this.statesOfUSA, screenName, 'State');
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): JsonCseDopModel {
        return {
            dopNumber: this.dropOffPoint.value,
            frontId: this.frontId,
            name1: this.name1.value,
            name2: this.name2.value,
            name1Long: this.name1Long,
            name2Long: this.name2Long,
            street: this.street.value,
            postcode: this.postalCode.value,
            city: this.city.value,
            state: this.state ? this.state.value : '',
            phone: this.phone.value,
            department: this.department.value,
            sortNumber: this.pos.value,
            region: this.region.value,
            floor: this.floor.value,
            building: this.building.value,
            room: this.room.value,
            contactPerson: this.contactPerson.value,
            addrNumber: this.addrNumber.value,
            stateOfUSA:
                this.statesOfUSA && this.statesOfUSA.selectedEntry
                    ? this.statesOfUSA.selectedEntry.model.value
                    : undefined,
        };
    }

    public static fromOData(data: ODataCseDopModel): CseDopModel {
        const cseDopModel = new CseDopModel(
            data.Dopnr,
            data.FrontId,
            data.Name1,
            data.Name2,
            '',
            '',
            data.Street,
            data.PostCode,
            data.City,
            data.State,
            data.Phone,
            data.Department,
            parseInt(data.SortNumber, 10),
            data.Region,
            data.Floor,
            data.Building,
        );
        AddressModel.updateStateOfUSA(cseDopModel, '', true);
        return cseDopModel;
    }

    public static fromAddressModel(address: AddressModel): CseDopModel {
        const cseDopModel = new CseDopModel(
            '',
            '',
            address.name1.value,
            address.name2.value,
            address.name1shortP58,
            address.name2shortP58,
            address.street.value,
            address.postCode.value,
            address.city.value,
            '',
            address.phone.value,
            address.department.value,
            0,
            '',
            address.floor.value,
            address.building.value,
            address.room.value,
            address.contactName1.value,
            address.addrNumber.value,
        );
        AddressModel.updateStateOfUSA(cseDopModel, address.statesOfUSA?.selectedEntry?.model.value);
        return cseDopModel;
    }

    public compareFromAddressModel(address: AddressModel): boolean {
        return (
            address.addrNumber.value === this.addrNumber.value &&
            (address.name1.value === this.name1.value || address.name1shortP58 === this.name1.value) &&
            (address.name2.value === this.name2.value || address.name2shortP58 === this.name2.value) &&
            address.street.value === this.street.value &&
            address.postCode.value === this.postalCode.value &&
            address.city.value === this.city.value &&
            address.phone.value === this.phone.value &&
            address.department.value === this.department.value &&
            address.floor.value === this.floor.value &&
            address.building.value === this.building.value
        );
    }

    // Migration logic to fix incorrectly nested addrNumber data.
    // addrNumber is expected to be a string, but older data may contain
    // deeply nested objects. Reset to empty string if it's not a string.
    public static resolveAddrNumber(input: unknown): string {
        const injector = CustomInjector.getInjector();
        const log = injector.get(LogInstanceToken);
        if (typeof input === 'string') {
            return input;
        }

        if (input && typeof input === 'object' && 'value' in input) {
            log.warn(`CseDopModel: resolveAddrNumber: Nested addrNumber detected and normalized during construction`);
            return this.resolveAddrNumber(input.value);
        }

        return '';
    }

    public getOutModel(): ODataCseDopOutModel {
        return super.getOutModel();
    }
}

export class CseDopModelFromJson extends CseDopModel {
    public constructor(data: JsonCseDopModel) {
        super(
            data.dopNumber,
            data.frontId,
            data.name1,
            data.name2,
            '',
            '',
            data.street,
            data.postcode,
            data.city,
            data.state,
            data.phone,
            data.department,
            data.sortNumber,
            data.region,
            data.floor,
            data.building,
            data.room,
            data.contactPerson,
            CseDopModel.resolveAddrNumber(data.addrNumber),
        );
        this.name1Long = data.name1Long;
        this.name2Long = data.name2Long;
        AddressModel.updateStateOfUSA(this, data.stateOfUSA);
    }
}

export interface JsonCseDopModel {
    dopNumber: string;
    frontId: string;
    name1: string;
    name2: string;
    street: string;
    postcode: string;
    city: string;
    state: string;
    phone: string;
    department: string;
    sortNumber: number | undefined;
    region: string;
    floor: string;
    building: string;
    room: string;
    contactPerson: string;
    addrNumber: string;
    name1Long: string;
    name2Long: string;
    stateOfUSA: string | undefined;
}

export interface ODataCseDopModel extends ODataObject {
    __metadata: {
        id: string;
        uri: string;
        type: 'HC_SM_NGM_58_SRV.CseDop';
    };
    Dopnr: string;
    FrontId: string;
    Name1: string;
    Name2: string;
    Street: string;
    PostCode: string;
    City: string;
    State: string;
    Phone: string;
    Department: string;
    SortNumber: string;
    Region: string;
    Floor: string;
    Building: string;
}

// need here type because empty interfaces are not allowed
export type ODataCseDopOutModel = ODataObject;
